import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, ChevronLeft, ChevronRight, Eye, Image, Type } from 'lucide-react';
import { storiesAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { getAvatarUrl } from '../../utils/helpers';
import './Stories.css';

const BG_COLORS = [
  '#6C63FF','#FF4D8D','#00D68F','#00B4D8',
  '#FF6B35','#FFB830','#8B5CF6','#EC4899',
  '#14B8A6','#F59E0B','#EF4444','#3B82F6',
];

const FONT_STYLES = ['normal','serif','mono','cursive'];

// ── Story Viewer ────────────────────────────────────────────────
function StoryViewer({ group, allGroups, groupIndex, onClose, onNext, onPrev }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [paused, setPaused]         = useState(false);
  const [progress, setProgress]     = useState(0);
  const timerRef   = useRef(null);
  const story = group.stories[currentIdx];
  const DURATION = 5000;

  useEffect(() => {
    if (story?._id) storiesAPI.view(story._id).catch(() => {});
  }, [story?._id]);

  // Wrapped in useCallback so it has a stable identity for the effect
  // below to depend on — fixes the "missing dependency" warning without
  // changing behavior.
  const handleNext = useCallback(() => {
    if (currentIdx < group.stories.length - 1) {
      setCurrentIdx(i => i + 1);
    } else if (groupIndex < allGroups.length - 1) {
      onNext();
    } else {
      onClose();
    }
  }, [currentIdx, group.stories.length, groupIndex, allGroups.length, onNext, onClose]);

  useEffect(() => {
    setProgress(0);
    if (paused) return;
    const interval = 50;
    const step     = (interval / DURATION) * 100;
    timerRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          handleNext();
          return 0;
        }
        return p + step;
      });
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [currentIdx, paused, handleNext]);

  const handlePrev = () => {
    if (currentIdx > 0) setCurrentIdx(i => i - 1);
    else if (groupIndex > 0) onPrev();
  };

  if (!story) return null;

  return (
    <div className="story-viewer-overlay" onClick={onClose}>
      <div className="story-viewer" onClick={e => e.stopPropagation()}>
        {/* Progress bars */}
        <div className="story-progress-bars">
          {group.stories.map((_, i) => (
            <div key={i} className="story-progress-track">
              <div className="story-progress-fill"
                style={{ width: i < currentIdx ? '100%' : i === currentIdx ? `${progress}%` : '0%' }} />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="story-viewer-header">
          <img src={getAvatarUrl(group.author)} alt="" className="avatar" style={{ width:36, height:36 }} />
          <div>
            <p style={{ fontWeight:600, fontSize:'0.875rem', color:'white' }}>{group.author.displayName}</p>
            <p style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.6)' }}>
              {new Date(story.createdAt).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
            </p>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <span style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', gap:4 }}>
              <Eye size={14}/> {story.viewCount || 0}
            </span>
            <button className="story-close-btn" onClick={onClose}><X size={20}/></button>
          </div>
        </div>

        {/* Story content */}
        <div className="story-content-area"
          style={{ background: story.type === 'text' ? story.backgroundColor : '#000' }}
          onMouseDown={() => setPaused(true)}
          onMouseUp={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {story.type === 'image' && story.mediaUrl && (
            <img src={story.mediaUrl} alt="" className="story-media" />
          )}
          {story.type === 'video' && story.mediaUrl && (
            <video src={story.mediaUrl} className="story-media" autoPlay muted loop />
          )}
          {story.content && (
            <div className="story-text-overlay" style={{
              fontFamily: story.fontStyle === 'serif' ? 'Georgia,serif' :
                          story.fontStyle === 'mono'  ? 'monospace' :
                          story.fontStyle === 'cursive' ? 'cursive' : 'inherit'
            }}>
              {story.content}
            </div>
          )}
        </div>

        {/* Nav zones */}
        <div className="story-nav-left"  onClick={handlePrev} />
        <div className="story-nav-right" onClick={handleNext} />
      </div>

      {/* Side nav */}
      {groupIndex > 0 && (
        <button className="story-side-btn left" onClick={e => { e.stopPropagation(); onPrev(); }}>
          <ChevronLeft size={28}/>
        </button>
      )}
      {groupIndex < allGroups.length - 1 && (
        <button className="story-side-btn right" onClick={e => { e.stopPropagation(); onNext(); }}>
          <ChevronRight size={28}/>
        </button>
      )}
    </div>
  );
}

// ── Story Creator ───────────────────────────────────────────────
function StoryCreator({ onClose, onCreated }) {
  const [tab, setTab]       = useState('text'); // text | image
  const [content, setContent] = useState('');
  const [bgColor, setBgColor] = useState('#6C63FF');
  const [fontStyle, setFont]  = useState('normal');
  const [creating, setCreate] = useState(false);

  const handleCreate = async () => {
    if (!content.trim() && tab === 'text') return;
    setCreate(true);
    try {
      const { data } = await storiesAPI.create({
        type: tab, content, backgroundColor: bgColor, fontStyle,
      });
      onCreated(data);
      onClose();
    } catch (e) { console.error(e); }
    finally { setCreate(false); }
  };

  return (
    <div className="story-creator-overlay" onClick={onClose}>
      <div className="story-creator" onClick={e => e.stopPropagation()}>
        <div className="story-creator-header">
          <h2>Create Story</h2>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>

        {/* Type tabs */}
        <div className="story-type-tabs">
          {[
            { key:'text', icon:<Type size={16}/>, label:'Text' },
            { key:'image', icon:<Image size={16}/>, label:'Photo/Video' },
          ].map(t => (
            <button key={t.key} className={`story-type-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="story-preview" style={{ background: tab === 'text' ? bgColor : '#111' }}>
          {tab === 'text' ? (
            <textarea
              className="story-text-input"
              placeholder="Type something..."
              value={content}
              onChange={e => setContent(e.target.value)}
              maxLength={200}
              style={{
                fontFamily: fontStyle === 'serif' ? 'Georgia,serif' :
                            fontStyle === 'mono'  ? 'monospace' :
                            fontStyle === 'cursive' ? 'cursive' : 'inherit'
              }}
            />
          ) : (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
              <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(255,255,255,0.1)',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Image size={32} color="rgba(255,255,255,0.5)"/>
              </div>
              <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.85rem' }}>
                Photo/video upload coming soon
              </p>
              <input placeholder="Add a caption..." value={content} onChange={e => setContent(e.target.value)}
                style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)',
                  borderRadius:8, padding:'8px 12px', color:'white', textAlign:'center', width:'80%' }}/>
            </div>
          )}
        </div>

        {/* Controls */}
        {tab === 'text' && (
          <>
            {/* Background colors */}
            <div className="story-colors">
              {BG_COLORS.map(c => (
                <button key={c} className={`story-color-btn ${bgColor === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setBgColor(c)} />
              ))}
            </div>
            {/* Font styles */}
            <div className="story-fonts">
              {FONT_STYLES.map(f => (
                <button key={f} className={`story-font-btn ${fontStyle === f ? 'active' : ''}`}
                  style={{ fontFamily: f === 'serif' ? 'Georgia,serif' : f === 'mono' ? 'monospace' :
                           f === 'cursive' ? 'cursive' : 'inherit' }}
                  onClick={() => setFont(f)}>Aa</button>
              ))}
            </div>
          </>
        )}

        <button className="btn btn-primary w-full" onClick={handleCreate} disabled={creating || (!content.trim() && tab === 'text')}>
          {creating ? 'Sharing...' : '✨ Share Story'}
        </button>
      </div>
    </div>
  );
}

// ── Main Stories Component ──────────────────────────────────────
export default function Stories() {
  const { user }    = useAuth();
  const [groups, setGroups]       = useState([]);
  const [viewing, setViewing]     = useState(null); // group index
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    storiesAPI.getAll()
      .then(({ data }) => setGroups(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCreated = (story) => {
    const myGroup = groups.find(g => g.author._id === (user._id || user.id));
    if (myGroup) {
      setGroups(prev => prev.map(g =>
        g.author._id === (user._id || user.id)
          ? { ...g, stories: [story, ...g.stories] }
          : g
      ));
    } else {
      setGroups(prev => [{ author: user, stories: [story], hasUnread: false }, ...prev]);
    }
  };

  return (
    <>
      <div className="stories-bar">
        {/* Add story button */}
        <div className="story-add-btn" onClick={() => setShowCreate(true)}>
          <div className="story-add-avatar">
            <img src={getAvatarUrl(user)} alt="" className="avatar" style={{ width:'100%', height:'100%' }}/>
            <div className="story-add-plus"><Plus size={14}/></div>
          </div>
          <span>Your story</span>
        </div>

        {/* Story bubbles */}
        {loading ? (
          Array(5).fill(0).map((_, i) => (
            <div key={i} className="story-bubble-wrap">
              <div className="skeleton" style={{ width:60, height:60, borderRadius:'50%' }}/>
              <div className="skeleton" style={{ width:48, height:10, borderRadius:4 }}/>
            </div>
          ))
        ) : groups.map((group, idx) => (
          <div key={group.author._id || idx} className="story-bubble-wrap" onClick={() => setViewing(idx)}>
            <div className={`story-bubble-ring ${group.hasUnread ? 'unread' : 'seen'}`}>
              <img src={getAvatarUrl(group.author)} alt="" className="avatar story-bubble-img" />
            </div>
            <span className="story-bubble-name">{group.author.displayName?.split(' ')[0]}</span>
          </div>
        ))}
      </div>

      {/* Viewer */}
      {viewing !== null && (
        <StoryViewer
          group={groups[viewing]}
          allGroups={groups}
          groupIndex={viewing}
          onClose={() => setViewing(null)}
          onNext={() => setViewing(i => Math.min(i + 1, groups.length - 1))}
          onPrev={() => setViewing(i => Math.max(i - 1, 0))}
        />
      )}

      {/* Creator */}
      {showCreate && (
        <StoryCreator onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
    </>
  );
}